import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../api/http";
import {
  fetchAvailableShifts,
  fetchStaffShifts,
  type StaffShiftSummary,
} from "../../../api/staffApi";
import { EmptyState } from "../components/EmptyState";
import { ShiftCard } from "../components/ShiftCard";
import { SkeletonCard } from "../components/SkeletonCard";
import { StaffAlert } from "../components/StaffAlert";
import { StaffAppShell } from "../components/StaffAppShell";

type Tab = "upcoming" | "past" | "available";

export function StaffShiftsPage() {
  const [tab, setTab] = useState<Tab>("upcoming");
  const [items, setItems] = useState<StaffShiftSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (active: Tab) => {
    setLoading(true);
    setError(null);
    try {
      const data =
        active === "available" ? await fetchAvailableShifts() : await fetchStaffShifts(active);
      setItems(data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load shifts.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(tab);
  }, [tab, load]);

  return (
    <StaffAppShell title="Shifts">
      <div className="staff-page">
        <div className="staff-tabs" role="tablist">
          {(
            [
              ["upcoming", "Upcoming"],
              ["past", "Past"],
              ["available", "Available"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`staff-tab${tab === id ? " staff-tab--active" : ""}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {error ? <StaffAlert onDismiss={() => setError(null)}>{error}</StaffAlert> : null}

        {loading ? (
          <div className="staff-card-stack">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={tab === "available" ? "No eligible open shifts" : `No ${tab} shifts`}
            detail={
              tab === "available"
                ? "When shifts match your roles and availability, they appear here."
                : undefined
            }
          />
        ) : (
          <div className="staff-card-stack">
            {items.map((shift) => (
              <ShiftCard key={shift.id} shift={shift} to={`/staff/shifts/${shift.id}`} />
            ))}
          </div>
        )}
      </div>
    </StaffAppShell>
  );
}
